<template>
  <MetaInfo
    :title="meta.title"
    :description="meta.description"
  ></MetaInfo>

  <div class="support-page">
    <navigation></navigation>

    <!-- Left Sidebar -->
    <support-sidebar></support-sidebar>

    <div class="support-content">
      <div class="container-fluid">
        <div class="row">
          <div class="col-sm-12">
          <!-- Error Message -->
          <div v-if="error" class="support-error">
            <h3>Error Loading Category</h3>
            <p><strong>{{ error.message }}</strong></p>
            <p v-if="error.isAuthError">
              Please check that your HelpScout API key is correctly configured in your <code>.env.local</code> file.
            </p>
          </div>

          <!-- Loading State -->
          <div v-if="loading && !error">
            <skeleton-loader type="category" :item-count="5"></skeleton-loader>
          </div>

          <!-- Category Content -->
          <div v-if="!loading && !error && category" class="support-category-page">
            <div class="support-category-page__header">
              <h2>{{ category.name }}</h2>
              <p v-if="category.description" class="support-category-page__description">
                {{ category.description }}
              </p>
            </div>

            <!-- Articles in this category -->
            <div v-if="articles.length > 0" class="support-category-page__articles">
              <ul class="support-article-list">
                <li
                  v-for="article in articles" 
                  :key="article.id"
                  class="support-article-list__item-wrapper"
                >
                  <router-link
                    :to="`/support/${$route.params.collectionId}/${article.slug || article.id}`"
                    class="support-article-list__item"
                  >
                    <div class="support-article-list__content">
                      <div class="support-article-list__details">
                        <p class="support-article-list__title">
                          {{ article.name || article.title }}
                        </p>
                        <p v-if="article.preview" class="support-article-list__preview">
                          {{ article.preview }}
                        </p>
                      </div>
                    </div>
                  </router-link>
                </li>
              </ul>
            </div>

            <!-- No articles message -->
            <div v-else class="support-empty">
              <p>No articles in this category yet.</p>
            </div>
          </div>

          <!-- Category not found -->
          <div v-if="!loading && !error && !category" class="support-empty">
            <h1>Category Not Found</h1>
            <p>The requested category could not be found.</p>
            <router-link :to="`/support/${$route.params.collectionId}`">
              ← Back to collection
            </router-link>
          </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MetaInfo from "@/components/MetaInfo";
import Navigation from "@/components/navigation/Main";
import SupportSidebar from "@/components/Support/SupportSidebar";
import SkeletonLoader from "@/components/Support/SkeletonLoader";
import helpScoutService from "@/services/helpscout";
import helpScoutState from "@/services/helpscoutState";

export default {
  name: "SupportCategoryPage",
  components: {
    MetaInfo,
    Navigation,
    SupportSidebar,
    SkeletonLoader,
  },
  data() {
    return {
      category: null,
      articles: [],
      loading: true,
      error: null,
      debugData: null,
      meta: {
        title: "",
        description: "",
      },
    };
  },
  async created() {
    await this.loadCategory();
  },
  watch: {
    '$route.params': {
      handler() {
        this.loadCategory();
      },
    },
  },
  methods: {
    async loadCategory() {
      try {
        this.loading = true;
        this.error = null;
        this.category = null;
        this.articles = [];
        this.meta.title = "";
        this.meta.description = "";

        const collectionIdentifier = this.$route.params.collectionId;
        const categoryIdentifier = this.$route.params.categoryId;

        console.log('Loading category:', categoryIdentifier, 'from collection:', collectionIdentifier);

        // Use shared state to find the collection
        await helpScoutState.getCollections(helpScoutService);
        const foundCollection = helpScoutState.findCollection(collectionIdentifier);

        if (foundCollection) {
          // Use shared state to get categories (with caching)
          const categories = await helpScoutState.getCategories(foundCollection.id, helpScoutService);
          
          // Find the category by ID or slug
          const foundCategory = categories.find(
            cat => cat.id === categoryIdentifier || cat.slug === categoryIdentifier
          );

          if (foundCategory) {
            console.log('Found category:', foundCategory);
            
            // Now fetch the full category details with articles using the ID
            try {
              const categoryData = await helpScoutService.getCategory(foundCategory.id);
              
              if (categoryData) {
                this.category = categoryData;
                
                this.meta.title = `${this.category.name} - Support`;
                this.meta.description =
                  this.category.description ||
                  `Browse ${this.category.name} support documentation and help articles.`;

                // Get articles - they might be in the category data
                if (this.category.articles && this.category.articles.length > 0) {
                  // Filter to only show published articles
                  this.articles = this.category.articles.filter(
                    article => article.status === 'published'
                  );
                } else {
                  // Try fetching articles by category endpoint
                  try {
                    const articlesData = await helpScoutService.getArticlesByCategory(foundCategory.id);
                    if (articlesData) {
                      this.articles = articlesData;
                    }
                  } catch (err) {
                    console.warn('Could not load articles for category:', err);
                  }
                }

                this.debugData = {
                  category: this.category,
                  articles: this.articles,
                };
              }
            } catch (err) {
              console.error('Error fetching category details:', err);
              // Even if the detail fetch fails, we can still show basic info
              this.category = foundCategory;
              this.meta.title = `${this.category.name} - Support`;
              this.meta.description =
                this.category.description ||
                `Browse ${this.category.name} support documentation and help articles.`;
            }
          }
        }

        if (!this.meta.title) {
          this.meta.title = "Support Documentation";
        }
        if (!this.meta.description) {
          this.meta.description =
            "Browse our support documentation and help articles.";
        }

      } catch (error) {
        console.error('Error loading category:', error);
        
        const isAuthError = error.response && (error.response.status === 401 || error.response.status === 403);
        
        this.error = {
          message: error.message || 'Failed to load category',
          isAuthError,
          details: error.response ? {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          } : null
        };

        this.debugData = { error: this.error };
        if (!this.meta.title) {
          this.meta.title = "Support Documentation";
        }
        if (!this.meta.description) {
          this.meta.description =
            "Browse our support documentation and help articles.";
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

